import React, { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Mail, AlertTriangle } from 'lucide-react'
import Card from '../components/shared/Card'
import Button from '../components/shared/Button'
import DocumentItem from '../components/shared/DocumentItem'
import SafeHTMLRenderer from '../components/shared/SafeHTMLRenderer'
import { useSimpleApi } from '../hooks/useApi'
import { useNotification } from '../context/NotificationContext'
import { formatDate as formatDateHelper } from '../utils/helpers'
import apiClient from '../services/api'

/**
 * Update View Page - displays full investor update details
 * Shows complete update message, date, company info, and navigation
 * Mobile-first responsive design with clean typography
 */
const UpdateViewPage = () => {
  // Get route parameters for startupId and updateId
  const { startupId, updateId } = useParams()
  const navigate = useNavigate()
  
  // API state for fetching update details - using simplified hook
  const { 
    loading, 
    error, 
    data: updateData, 
    execute: fetchUpdate 
  } = useSimpleApi()
  
  const { showError } = useNotification()

  // Fetch update details on component mount
  useEffect(() => {
    if (startupId && updateId) {
      fetchUpdate(() => apiClient.getUpdate(startupId, updateId))
    }
  }, [startupId, updateId, fetchUpdate])

  // Show error notification when API fails
  useEffect(() => {
    if (error) {
      showError('Unable to load update details. Please try again later.', 'Update Error')
    }
  }, [error, showError])

  // Format date for display using helpers
  const formatDate = (dateString) => {
    if (!dateString) return 'Date not available'
    return formatDateHelper(dateString, 'MMMM dd, yyyy')
  }

  // Format time for display using helpers
  const formatTime = (dateString) => {
    if (!dateString) return ''
    return formatDateHelper(dateString, 'h:mm a')
  }



  return (
    <div className="bg-gray-50 p-4 pt-0 md:p-1 lg:p-4 xl:p-2">
      {/* Header with back navigation */}
      <div className="mb-4">
      <Button
            variant="outline"
            size="small"
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Button>
      </div>

      {/* Main content */}
      {/* <div className="max-w-4xl mx-auto"> */}
        {loading ? (
          <Card 
            className="p-6" 
            shadow="medium"
            title="Portfolio Company Update"
            >
            <div className="flex items-center justify-center">
              <div className="text-gray-500">Loading update details...</div>
            </div>
          </Card>
        ) : error ? (
          <Card className="p-6" shadow="medium">
            <div className="text-center">
              <div className="text-red-600 mb-4">Unable to load update</div>
              <Button 
                variant="primary" 
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </div>
          </Card>
        ) : updateData ? (
          <div className="space-y-6">
            {/* Email Client Style Header */}
            <Card className="p-0" shadow="medium" title="Portfolio Company Update" contentClassName="space-y-0">
              {/* Email Header Section */}
              <div className="bg-white border border-gray-200 rounded-t-lg">
                {/* Subject Line */}
                <div className="px-6 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 text-sm">
                      <span className="font-semibold text-gray-900">Subject:</span>
                      <span className="ml-2 text-gray-700">
                        {updateData.subject || 'Portfolio Company Update'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* From Line */}
                <div className="px-6 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 text-sm">
                      <span className="font-semibold text-gray-900">From:</span>
                      <span className="ml-2 text-gray-700">{updateData.companyName}</span>
                      {/* <span className="ml-2 text-blue-600">
                        &lt;no-reply@portfoliocompany.com&gt;
                      </span> */}
                    </div>
                  </div>
                </div>

                {/* Security Warning Box */}
                <div className="mx-6 my-3 p-4 bg-gray-50 border border-gray-300 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Mail className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-gray-700 text-sm">
                      <strong>Security Notice:</strong> This email is directly from a portfolio company and has not been reviewed by the Startup TNT team. Please use caution when opening links or attachments.
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Content Section */}
              <div className="p-2 xl:p-4 bg-gray-50 rounded-b-lg">
                <div className="space-y-4">

                   {/* Email content */}
                   <div className="flex-1">
                     <SafeHTMLRenderer
                       html={updateData.fullMessage || 'No update content available.'}
                       title={`Portfolio Update from ${updateData.companyName}`}
                       allowImages={true}
                       allowLinks={true}
                       maxWidth="100%"
                       className="bg-white border border-gray-200 rounded-lg overflow-hidden h-full"
                       style={{ minHeight: '200px', height: '50vh' }}
                     />
                   </div>
                </div>
              </div>
            </Card>

            {/* Documents section - only show if documents exist */}
            {updateData.documents && updateData.documents.length > 0 && (
              <Card className="p-6" shadow="medium">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Attached Documents
                  </h3>
                  
                  {/* Documents grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {updateData.documents.map((document) => (
                      <DocumentItem 
                        key={document.id}
                        document={document}
                      />
                    ))}
                  </div>
                </div>
              </Card>
            )}
          </div>
        ) : (
          <Card className="p-8" shadow="medium">
            <div className="text-center">
              <div className="text-gray-500 mb-4">No update data available</div>
              <Button 
                variant="primary" 
                onClick={() => navigate('/dashboard')}
              >
                Return to Dashboard
              </Button>
            </div>
          </Card>
        )}
    </div>
  )
}

export default UpdateViewPage